import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   Particules à TRAJECTOIRE ANALYTIQUE (PLAN.md §4.7, couche 2).

   L'idée, et c'est la meilleure du dossier technique : les débris d'un shmup
   sont des points balistiques avec frottement. Leur position est une FONCTION
   FERMÉE DU TEMPS, pas le résultat d'une intégration pas à pas.

       p(t) = p0 + v0·(1 − e^(−k·t))/k + g·(t − (1 − e^(−k·t))/k)/k

   Conséquence : à l'émission, on écrit UNE SEULE FOIS les attributs de la
   particule. Ensuite, plus rien ne remonte jamais du CPU — le vertex shader
   évalue p(t) avec le temps comme unique uniforme.

     · zéro simulation CPU
     · zéro upload par frame
     · zéro allocation
     · un seul draw call pour tout le champ

   Le tri en profondeur est inutile en additif : l'addition étant commutative,
   l'ordre de dessin n'affecte pas le résultat.

   Ce qu'on perd honnêtement : pas de collision avec le décor, pas de forces
   dynamiques, pas de turbulence procédurale. Sur des débris qui volent 400 ms
   dans le vide, personne ne le remarquera.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Coefficient de frottement. Plus il est grand, plus les débris freinent vite
 *  et plus la gerbe paraît « sèche ». */
const DRAG = 3.2;
/** Gravité, en unités/s². Négative : le monde a le haut vers +Y. */
const GRAVITY = -420;

const VERT = /* glsl */ `
attribute vec3 iP0;
attribute vec3 iV0;
attribute float iBirth;
attribute float iLife;
attribute float iSize;
attribute float iScrollBirth;
attribute vec3 iTint;

uniform float uTime;
uniform float uScroll;

varying float vAge;
varying vec3 vTint;
varying vec2 vLocal;

void main() {
  vLocal = uv;
  float t = uTime - iBirth;

  // Particule morte ou pas encore née : on effondre le quad sur un point.
  // Un triangle dégénéré ne produit aucun fragment — c'est la façon la moins
  // chère d'« éteindre » une instance sans rien remonter au CPU.
  float alive = step(0.0, t) * step(t, iLife);

  vAge = clamp(t / iLife, 0.0, 1.0);
  vTint = iTint;

  float k = ${DRAG.toFixed(4)};
  float e = (1.0 - exp(-k * t)) / k;
  vec3 g = vec3(0.0, ${GRAVITY.toFixed(1)}, 0.0);

  vec3 p = iP0 + iV0 * e + g * ((t - e) / k);

  // ⚠️ Le monde DÉFILE. Sans correction, les débris resteraient figés dans le
  // référentiel de la caméra et « glisseraient » visiblement par rapport au
  // décor. Et comme la vitesse de défilement varie (elle s'arrête sur un boss),
  // la position dépend de l'intégrale de l'historique de scroll, que le shader
  // ne connaît pas : on stocke donc le décalage AU MOMENT DE LA NAISSANCE et
  // on ne transmet que le décalage courant.
  p.y -= (uScroll - iScrollBirth);

  // La particule rétrécit en fin de vie plutôt que de disparaître d'un coup.
  float size = iSize * alive * (1.0 - vAge * 0.75);

  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  mv.xy += position.xy * size;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
precision highp float;
varying float vAge;
varying vec3 vTint;
varying vec2 vLocal;

void main() {
  // Disque à bord adouci, calculé analytiquement : aucune texture, donc aucun
  // octet à charger, aucun transcodage, et un bord net à toute résolution.
  float r = length(vLocal - 0.5) * 2.0;
  float disc = 1.0 - smoothstep(0.55, 1.0, r);

  // Extinction quadratique : l'étincelle s'éteint vite puis s'attarde, ce qui
  // se lit comme une braise plutôt que comme un fondu linéaire.
  float fade = 1.0 - vAge;
  fade *= fade;

  // Surbrillance en début de vie : c'est elle qui déclenche le bloom et donne
  // à la gerbe son éclat, sans aucune lumière dynamique.
  gl_FragColor = vec4(vTint * (1.0 + fade * 2.5), disc * fade);
}
`;

export interface SparkOptions {
  readonly capacity?: number;
  readonly renderOrder?: number;
}

export class SparkField {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly aP0: THREE.InstancedBufferAttribute;
  private readonly aV0: THREE.InstancedBufferAttribute;
  private readonly aBirth: THREE.InstancedBufferAttribute;
  private readonly aLife: THREE.InstancedBufferAttribute;
  private readonly aSize: THREE.InstancedBufferAttribute;
  private readonly aScroll: THREE.InstancedBufferAttribute;
  private readonly aTint: THREE.InstancedBufferAttribute;
  private readonly uniforms: { uTime: { value: number }; uScroll: { value: number } };

  private readonly capacity: number;
  /** Curseur d'anneau. Les particules les plus anciennes sont écrasées les
   *  premières — ce qui est exactement le comportement voulu sous forte charge :
   *  on sacrifie une gerbe qui s'éteignait, jamais une qui vient de naître. */
  private head = 0;
  /** Bornes de la plage modifiée depuis le dernier `flush()`. */
  private dirtyLo = Infinity;
  private dirtyHi = -Infinity;

  constructor(opts: SparkOptions = {}) {
    this.capacity = opts.capacity ?? 4096;
    const n = this.capacity;

    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.attributes['position'] = quad.attributes['position']!;
    this.geo.attributes['uv'] = quad.attributes['uv']!;
    this.geo.instanceCount = n;

    const inst = (size: number): THREE.InstancedBufferAttribute => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(n * size), size);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };

    this.aP0 = inst(3);
    this.aV0 = inst(3);
    this.aBirth = inst(1);
    this.aLife = inst(1);
    this.aSize = inst(1);
    this.aScroll = inst(1);
    this.aTint = inst(3);

    // Toutes les particules naissent « déjà mortes » : durée de vie nulle.
    this.aLife.array.fill(0);

    this.geo.setAttribute('iP0', this.aP0);
    this.geo.setAttribute('iV0', this.aV0);
    this.geo.setAttribute('iBirth', this.aBirth);
    this.geo.setAttribute('iLife', this.aLife);
    this.geo.setAttribute('iSize', this.aSize);
    this.geo.setAttribute('iScrollBirth', this.aScroll);
    this.geo.setAttribute('iTint', this.aTint);

    this.uniforms = { uTime: { value: 0 }, uScroll: { value: 0 } };

    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    /* Un `ShaderMaterial` n'a pas besoin de clé de cache pour être correct —
       three intègre déjà les sources GLSL à la sienne. On en pose une quand
       même : le compteur de programmes du warm-up est notre seul instrument de
       mesure, et un programme anonyme y est indiscernable d'une fuite. */
    mat.customProgramCacheKey = (): string => 'sparkfield-v1';

    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = opts.renderOrder ?? 1500;
  }

  /**
   * Émet une gerbe. C'est le SEUL moment où l'on écrit dans les tampons.
   *
   * `rand` est fourni par l'appelant — typiquement le RNG visuel du monde, pour
   * que les effets ne consomment jamais le flux aléatoire du gameplay et qu'on
   * puisse ajouter ou retirer une gerbe sans invalider un replay.
   */
  burst(
    x: number,
    y: number,
    count: number,
    speedMin: number,
    speedMax: number,
    life: number,
    size: number,
    tint: THREE.Color,
    now: number,
    scroll: number,
    rand: () => number,
  ): void {
    for (let i = 0; i < count; i++) {
      const idx = this.head;
      this.head = (this.head + 1) % this.capacity;

      const a = rand() * Math.PI * 2;
      const sp = speedMin + rand() * (speedMax - speedMin);

      const p3 = idx * 3;
      this.aP0.array[p3] = x;
      this.aP0.array[p3 + 1] = y;
      this.aP0.array[p3 + 2] = 2;

      this.aV0.array[p3] = Math.cos(a) * sp;
      this.aV0.array[p3 + 1] = Math.sin(a) * sp;
      this.aV0.array[p3 + 2] = 0;

      this.aTint.array[p3] = tint.r;
      this.aTint.array[p3 + 1] = tint.g;
      this.aTint.array[p3 + 2] = tint.b;

      this.aBirth.array[idx] = now;
      this.aLife.array[idx] = life * (0.7 + rand() * 0.6);
      this.aSize.array[idx] = size * (0.6 + rand() * 0.8);
      this.aScroll.array[idx] = scroll;

      if (idx < this.dirtyLo) this.dirtyLo = idx;
      if (idx > this.dirtyHi) this.dirtyHi = idx;
    }
  }

  /**
   * Une frame. Ne remonte RIEN au GPU s'il n'y a pas eu d'émission : c'est le
   * critère du jalon — zéro upload de tampon de particules par frame.
   */
  update(now: number, scroll: number): void {
    this.uniforms.uTime.value = now;
    this.uniforms.uScroll.value = scroll;

    if (this.dirtyHi < this.dirtyLo) return; // rien de neuf, rien à envoyer

    // Envoi partiel : seule la plage réellement écrite remonte. Sur une gerbe
    // de 30 particules dans un champ de 4 096, on envoie 0,7 % du tampon.
    for (const a of [this.aP0, this.aV0, this.aBirth, this.aLife, this.aSize, this.aScroll, this.aTint]) {
      a.clearUpdateRanges();
      const items = a.itemSize;
      a.addUpdateRange(this.dirtyLo * items, (this.dirtyHi - this.dirtyLo + 1) * items);
      a.needsUpdate = true;
    }

    this.dirtyLo = Infinity;
    this.dirtyHi = -Infinity;
  }

  /** Nombre d'instances allouées — la capacité, pas le nombre de vivantes :
   *  celles qui sont mortes s'effondrent dans le shader et ne coûtent qu'un
   *  triangle dégénéré. */
  get instanceCount(): number {
    return this.capacity;
  }

  /** Position courante du curseur d'anneau. Exposée pour les tests. */
  get cursor(): number {
    return this.head;
  }
}

/** Position analytique d'une particule, en CPU — utilisée uniquement par les
 *  tests, pour vérifier que la formule du shader est bien celle qu'on croit. */
export function analyticPosition(
  p0x: number,
  p0y: number,
  v0x: number,
  v0y: number,
  t: number,
): [number, number] {
  const k = DRAG;
  const e = (1 - Math.exp(-k * t)) / k;
  return [p0x + v0x * e, p0y + v0y * e + GRAVITY * ((t - e) / k)];
}

export const SPARK_DRAG = DRAG;
export const SPARK_GRAVITY = GRAVITY;
